import { ComponentService } from '../Services/Axios/Api';
import { generateDynamicCSS } from '../ComponentsMap';

export const CARD_IMAGE_STYLES = ['circle', 'fill'];
export const CARD_BORDER_RADIUS = ['rounded', 'normal'];
export const CARD_TEXT_ALIGN = ['center', 'justified'];
export const SECTION_CARDS_LAYOUT = ['normal', 'center'];

export const DEFAULT_CARD_STYLE = {
    titleColor: '#212529',
    descriptionColor: '#6c757d',
    imageStyle: 'circle',
    borderRadius: 'rounded',
    textAlign: 'center',
    backgroundColor: '#ffffff',
    backgroundImage: '',
};

export const DEFAULT_SECTION_STYLE = {
    backgroundColor: '#ffffff',
    backgroundImage: '',
    cardsLayout: 'normal',
};

const createDefaultCard = (id, order = 1) => ({
    id,
    order,
    imageUrl: '',
    title: 'Card Title',
    description: 'Add a short description for this card.',
    useCustomStyle: false,
    style: { ...DEFAULT_CARD_STYLE },
});

export const sortCardsByOrder = (cards) =>
    [...(cards || [])].sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0));

/** Assigns order from current array position (does not re-sort — avoids undoing swaps). */
export const normalizeCardOrders = (cards) =>
    (cards || []).map((card, index) => ({ ...card, order: index + 1 }));

class CardsSection {
    data = {};

    constructor() {
        this.data = {
            id: '',
            ComponentName: 'CardsSection',
            sectionTitle: '',
            cards: [createDefaultCard(1)],
            style: {
                CardsSection: { ...DEFAULT_SECTION_STYLE },
                CardsSectionTitle: {
                    color: '#212529',
                },
                CardsSectionCard: { ...DEFAULT_CARD_STYLE },
            },
        };
    }

    getId() { return this.data.id; }
    setId(id) { this.data.id = id; }

    getComponentName() { return this.data.ComponentName; }
    setComponentName(name) { this.data.ComponentName = name; }

    getSectionTitle() { return this.data.sectionTitle || ''; }
    setSectionTitle(title) { this.data.sectionTitle = title; }

    getCards() { return sortCardsByOrder(this.data.cards || []); }
    setCards(cards) { this.data.cards = normalizeCardOrders(cards); }

    addCard(card) {
        const existing = this.getCards();
        const nextOrder = existing.length
            ? Math.max(...existing.map((c) => Number(c.order) || 0)) + 1
            : 1;
        const newCard = card || createDefaultCard(Date.now(), nextOrder);
        this.data.cards = normalizeCardOrders([...existing, newCard]);
    }

    moveCard(cardId, direction) {
        const sorted = this.getCards();
        const index = sorted.findIndex((c) => c.id === cardId);
        if (index < 0) return;
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= sorted.length) return;
        const next = [...sorted];
        [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
        this.setCards(next);
    }

    removeCard(id) {
        this.setCards(this.getCards().filter((c) => c.id !== id));
    }

    updateCard(id, updates) {
        this.data.cards = this.getCards().map((c) =>
            c.id === id ? { ...c, ...updates } : c
        );
    }

    getStyle() { return this.data.style; }
    setStyle(style) { this.data.style = style; }

    getGlobalCardStyle() {
        return { ...DEFAULT_CARD_STYLE, ...(this.data.style?.CardsSectionCard || {}) };
    }

    setGlobalCardStyle(cardStyle) {
        this.data.style = {
            ...this.getStyle(),
            CardsSectionCard: { ...DEFAULT_CARD_STYLE, ...cardStyle },
        };
    }

    getCardEffectiveStyle(card) {
        const global = this.getGlobalCardStyle();
        if (card?.useCustomStyle && card?.style) {
            return { ...global, ...card.style };
        }
        return global;
    }

    getData() { return this.data; }
    setData(data) {
        this.data = data;
        if (!this.data.cards?.length) {
            this.data.cards = [createDefaultCard(1)];
        }
        if (!this.data.style?.CardsSection) {
            this.data.style = {
                ...this.data.style,
                CardsSection: { ...DEFAULT_SECTION_STYLE },
            };
        } else {
            this.data.style.CardsSection = {
                ...DEFAULT_SECTION_STYLE,
                ...this.data.style.CardsSection,
            };
        }
        if (!this.data.style?.CardsSectionCard) {
            this.data.style = {
                ...this.data.style,
                CardsSectionCard: { ...DEFAULT_CARD_STYLE },
            };
        } else {
            this.data.style.CardsSectionCard = {
                ...DEFAULT_CARD_STYLE,
                ...this.data.style.CardsSectionCard,
            };
        }
        const withDefaults = (this.data.cards || []).map((card, index) => ({
            ...card,
            order: card.order ?? index + 1,
            style: { ...DEFAULT_CARD_STYLE, ...(card.style || {}) },
        }));
        this.data.cards = normalizeCardOrders(sortCardsByOrder(withDefaults));
    }

    getCardsLayout() {
        return this.data.style?.CardsSection?.cardsLayout || 'normal';
    }

    renderStyles() {
        const sectionId = this.getId();
        if (!sectionId) return;

        const sectionStyle = this.data.style?.CardsSection || {};
        const bgImage = sectionStyle.backgroundImage
            ? `url("${sectionStyle.backgroundImage}")`
            : 'none';

        generateDynamicCSS(`CardsSection-${sectionId}`, [
            {
                backgroundColor: sectionStyle.backgroundColor || '#ffffff',
                backgroundImage: bgImage,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
            },
        ]);

        generateDynamicCSS(`CardsSectionTitle-${sectionId}`, [
            { color: this.data.style?.CardsSectionTitle?.color || '#212529' },
        ]);

        const globalCard = this.getGlobalCardStyle();
        generateDynamicCSS(`CardsSectionCardGlobal-${sectionId}`, [
            { color: globalCard.titleColor },
        ]);
        generateDynamicCSS(`CardsSectionCardGlobalDesc-${sectionId}`, [
            { color: globalCard.descriptionColor },
        ]);

        const globalCardSurface = {
            backgroundColor: globalCard.backgroundColor || '#ffffff',
        };
        if (globalCard.backgroundImage) {
            globalCardSurface.backgroundImage = `url("${globalCard.backgroundImage}")`;
            globalCardSurface.backgroundSize = 'cover';
            globalCardSurface.backgroundPosition = 'center';
            globalCardSurface.backgroundRepeat = 'no-repeat';
        }
        generateDynamicCSS(`CardsSectionCardBgGlobal-${sectionId}`, [globalCardSurface]);

        this.getCards().forEach((card) => {
            const effective = this.getCardEffectiveStyle(card);
            if (card.useCustomStyle) {
                generateDynamicCSS(`CardsSectionCardTitle-${sectionId}-${card.id}`, [
                    { color: effective.titleColor },
                ]);
                generateDynamicCSS(`CardsSectionCardDesc-${sectionId}-${card.id}`, [
                    { color: effective.descriptionColor },
                ]);
                const cardSurface = {
                    backgroundColor: effective.backgroundColor || '#ffffff',
                };
                if (effective.backgroundImage) {
                    cardSurface.backgroundImage = `url("${effective.backgroundImage}")`;
                    cardSurface.backgroundSize = 'cover';
                    cardSurface.backgroundPosition = 'center';
                    cardSurface.backgroundRepeat = 'no-repeat';
                }
                generateDynamicCSS(`CardsSectionCardBg-${sectionId}-${card.id}`, [cardSurface]);
            }
        });
    }

    async load(id) {
        try {
            if (id) {
                const response = await ComponentService.getComponentById(id);
                const data = response.data ? JSON.parse(response.data) : {};
                this.setData(data);
                this.setId(response.id);
            } else {
                const response = await ComponentService.getComponent(this.data.ComponentName);
                const data = response[0]?.data ? JSON.parse(response[0].data) : {};
                this.setData(data);
                this.setId(response[0]?.id);
            }
            this.renderStyles();
            return this.data;
        } catch (error) {
            throw new Error(error.response?.data || 'Failed to load cards section data');
        }
    }

    async save() {
        try {
            const response = await ComponentService.SaveData(this.getId(), this.getData());
            this.renderStyles();
            return response;
        } catch (error) {
            throw new Error(error.response?.data || 'Failed to save cards section data');
        }
    }
}

export const createCardsSectionInstance = () => new CardsSection();
export { createDefaultCard };
